/**
 * @fileOverview The per-view walkthrough's catalog — which regions of which view get explained, in
 * what order. Pure data and lookups: no Vue imports, no DOM access. See docs/view-help.md.
 *
 * A **tour** belongs to a group of views that are read one after the other, and its steps carry the
 * route they are read on. That is what makes "help for this view" and "one walkthrough across the
 * feature" the same thing: the trigger on Prüfung opens the tour at Prüfung's first step and carries
 * the reader onwards into Auswertung, while the timeline still reaches back into Meine Zeiten.
 *
 * Deliberately a separate catalog from `services/onboardingTour.ts`, which is a linear list of video
 * clips with no geometry at all. Merging them would put this feature's shape back into that one.
 *
 * The tours themselves live in `tours/`, one file each; `types.ts` has their shape and `define.ts` the
 * helpers for writing one.
 */

import { catalogProblems, tourProblems } from "@/services/viewHelp/define"
import { CUSTOMERS_TOUR } from "@/services/viewHelp/tours/customers"
import { DAY_SUBMISSIONS_TOUR } from "@/services/viewHelp/tours/daysubmissions"
import { EMPLOYEES_TOUR } from "@/services/viewHelp/tours/employees"
import { FILES_TOUR } from "@/services/viewHelp/tours/files"
import { HOURLY_WAGE_INVOICE_TOUR } from "@/services/viewHelp/tours/hourlywageinvoice"
import { MACHINES_TOUR } from "@/services/viewHelp/tours/machines"
import { MATERIALS_TOUR } from "@/services/viewHelp/tours/materials"
import { PACKETS_TOUR } from "@/services/viewHelp/tours/packets"
import { PERSONNEL_PLANNING_TOUR } from "@/services/viewHelp/tours/personnelplanning"
import { PROJECT_COSTS_TOUR } from "@/services/viewHelp/tours/projectcosts"
import { PROJECTS_TOUR } from "@/services/viewHelp/tours/projects"
import { SETTINGS_TOUR } from "@/services/viewHelp/tours/settings"
import { SUPPLIERS_TOUR } from "@/services/viewHelp/tours/suppliers"
import { TIMETRACKING_TOUR } from "@/services/viewHelp/tours/timetracking"
import type { ViewHelpStep, ViewHelpTour } from "@/services/viewHelp/types"

/**
 * Every tour. A route is the trigger's route in at most one of them (`catalogProblems`); it may be a
 * related view in any number. A tour without steps is inert: the trigger does not render for it.
 */
export const VIEW_HELP_TOURS: ViewHelpTour[] = [
  PROJECTS_TOUR,
  PROJECT_COSTS_TOUR,
  HOURLY_WAGE_INVOICE_TOUR,
  CUSTOMERS_TOUR,
  EMPLOYEES_TOUR,
  MATERIALS_TOUR,
  MACHINES_TOUR,
  SUPPLIERS_TOUR,
  PACKETS_TOUR,
  PERSONNEL_PLANNING_TOUR,
  TIMETRACKING_TOUR,
  DAY_SUBMISSIONS_TOUR,
  FILES_TOUR,
  SETTINGS_TOUR,
]

// Cheap to check once, and loud where it is written: a broken tour otherwise only shows as a step that
// silently never plays.
if (import.meta.env.DEV) {
  const problems = [...catalogProblems(VIEW_HELP_TOURS), ...VIEW_HELP_TOURS.flatMap(tour => tourProblems(tour))]
  for (const problem of problems) console.error(`[view-help] ${problem}`)
}

/** The tour offered on a route, or null. A handful of tours, called from a computed. */
export function findTourForRoute(routeName?: string | null): ViewHelpTour | null {
  if (!routeName) return null
  return VIEW_HELP_TOURS.find(tour => tour.routes.includes(routeName)) ?? null
}

/**
 * Where a tour opens when it is started from `routeName`: that route's first step, or the very first
 * step if the route has none left after gating.
 *
 * Opening at the current view is the whole reason this reads as per-view help rather than as a tour
 * of the product. Somebody pressing Erklärung on Prüfung is asking about Prüfung; walking them back
 * to Meine Zeiten first would answer a question they did not ask.
 */
export function firstStepIndexForRoute(steps: ViewHelpStep[], routeName?: string | null): number {
  const index = steps.findIndex(step => step.route === routeName)
  return index === -1 ? 0 : index
}
